// Servicio de Depósitos/Sucursales (Warehouses)
// Gestiona los depósitos donde se almacena el stock

import { getCollection, getDocument, createDocument, updateDocument } from './firestore.service'
import { Timestamp } from 'firebase/firestore'

const WAREHOUSES_COLLECTION = 'warehouses'

/**
 * Obtener todos los depósitos
 * @returns {Promise<Array>} Lista de depósitos
 */
export const getAllWarehouses = async () => {
  try {
    // Obtener todos los depósitos y filtrar/ordenar en el cliente
    const allWarehouses = await getCollection(WAREHOUSES_COLLECTION, [])
    // Filtrar solo activos
    const activeWarehouses = allWarehouses.filter(w => w.active === true)
    // Ordenar por nombre
    activeWarehouses.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
    return activeWarehouses
  } catch (error) {
    console.error('Error al obtener depósitos:', error)
    throw error
  }
}

/**
 * Obtener todos los depósitos (incluyendo inactivos)
 * @returns {Promise<Array>} Lista de depósitos
 */
export const getAllWarehousesIncludingInactive = async () => {
  try {
    // Obtener todos los depósitos sin filtros y ordenar en el cliente
    const warehouses = await getCollection(WAREHOUSES_COLLECTION, [])
    // Ordenar por nombre
    warehouses.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
    return warehouses
  } catch (error) {
    console.error('Error al obtener depósitos:', error)
    throw error
  }
}

/**
 * Obtener un depósito por ID
 * @param {string} warehouseId - ID del depósito
 * @returns {Promise<Object|null>} Depósito o null
 */
export const getWarehouse = async (warehouseId) => {
  try {
    return await getDocument(WAREHOUSES_COLLECTION, warehouseId)
  } catch (error) {
    console.error('Error al obtener depósito:', error)
    throw error
  }
}

/**
 * Crear un nuevo depósito
 * @param {Object} warehouseData - Datos del depósito
 * @returns {Promise<string>} ID del depósito creado
 */
export const createWarehouse = async (warehouseData) => {
  try {
    const data = {
      name: warehouseData.name,
      address: warehouseData.address || '',
      phone: warehouseData.phone || '',
      email: warehouseData.email || '',
      active: warehouseData.active !== undefined ? warehouseData.active : true,
      notes: warehouseData.notes || '',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    }
    
    return await createDocument(WAREHOUSES_COLLECTION, data)
  } catch (error) {
    console.error('Error al crear depósito:', error)
    throw error
  }
}

/**
 * Actualizar un depósito
 * @param {string} warehouseId - ID del depósito
 * @param {Object} warehouseData - Datos a actualizar
 * @returns {Promise<void>}
 */
export const updateWarehouse = async (warehouseId, warehouseData) => {
  try {
    const data = {
      ...warehouseData,
      updatedAt: Timestamp.now()
    }
    
    return await updateDocument(WAREHOUSES_COLLECTION, warehouseId, data)
  } catch (error) {
    console.error('Error al actualizar depósito:', error)
    throw error
  }
}

/**
 * Eliminar (desactivar) un depósito
 * @param {string} warehouseId - ID del depósito
 * @returns {Promise<void>}
 */
export const deleteWarehouse = async (warehouseId) => {
  try {
    // En lugar de eliminar, desactivamos
    return await updateDocument(WAREHOUSES_COLLECTION, warehouseId, {
      active: false,
      updatedAt: Timestamp.now()
    })
  } catch (error) {
    console.error('Error al eliminar depósito:', error)
    throw error
  }
}

